'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { trackPickupEvent } from './pickupAnalytics';
import type { RingSizes } from 'content/products/ring/productMap/constants';
import type { RingProColors } from 'content/products/ring-pro';
import { BEST_BUY_PICKUP_ENABLED } from 'utils/featureFlags';
import {
  BB_DATE_RE,
  BB_PICKUP_GTM_EVENTS,
  BB_PICKUP_STORE_STORAGE_KEY,
  BB_PICKUP_ZIP_STORAGE_KEY,
  US_ZIP_RE,
} from './config';
import { readStoredHold, releaseStoredHold } from './pickupHold';
import { usePickupCatalogue } from './pickupCatalogue';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { pickupSkuFor } from './pickupSku';

/** What the shopper picked on the buy page. */
export type PickupChoice = {
  location_id: string;
  name: string;
  zip: string;
  sku: string;
  /** The store's pickup date for this SKU (YYYY-MM-DD), when it sent one. */
  pickup_date?: string;
};

export type PickupMode = 'ship' | 'pickup';

export type PickupSelection = {
  mode: PickupMode;
  store: PickupChoice | null;
  /** The shopper explicitly answered the delivery step (tapped a card).
   * Nothing is pre-answered, so the default 'ship' alone does not count. */
  answered: boolean;
  /** Bumped when add-to-cart finds pickup chosen without a store, so the
   * selector can scroll into view and say what is missing. */
  promptNonce: number;
};

const INITIAL: PickupSelection = {
  mode: 'ship',
  store: null,
  answered: false,
  promptNonce: 0,
};

let state: PickupSelection = INITIAL;
let hydrated = false;
const listeners = new Set<() => void>();

const persist = () => {
  try {
    sessionStorage.setItem(
      BB_PICKUP_STORE_STORAGE_KEY,
      JSON.stringify({
        mode: state.mode,
        store: state.store,
        answered: state.answered,
      }),
    );
  } catch {}
};

const set = (next: Partial<PickupSelection>) => {
  state = { ...state, ...next };
  persist();
  listeners.forEach((fn) => fn());
};

const readChoice = (raw: unknown): PickupChoice | null => {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Record<string, unknown>;
  if (
    typeof c.location_id === 'string' &&
    typeof c.name === 'string' &&
    typeof c.zip === 'string' &&
    US_ZIP_RE.test(c.zip) &&
    typeof c.sku === 'string'
  ) {
    return {
      location_id: c.location_id,
      name: c.name,
      zip: c.zip,
      sku: c.sku,
      ...(typeof c.pickup_date === 'string' && BB_DATE_RE.test(c.pickup_date)
        ? { pickup_date: c.pickup_date }
        : {}),
    };
  }
  return null;
};

/** Loads the stored choice once, after mount (the server render always shows
 * "Ship to me"). No-op with the flag off. */
export const ensurePickupSelectionHydrated = (): void => {
  if (hydrated || !BEST_BUY_PICKUP_ENABLED || typeof window === 'undefined') {
    return;
  }
  hydrated = true;
  try {
    const raw = sessionStorage.getItem(BB_PICKUP_STORE_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as {
      mode?: unknown;
      store?: unknown;
      answered?: unknown;
    };
    const store = readChoice(parsed.store);
    state = {
      ...state,
      mode: parsed.mode === 'pickup' ? 'pickup' : 'ship',
      store,
      // Older stored JSON has no flag: not answered.
      answered: parsed.answered === true,
    };
    listeners.forEach((fn) => fn());
  } catch {}
};

export const getPickupSelection = (): PickupSelection => state;

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export const usePickupSelection = (): PickupSelection =>
  useSyncExternalStore(subscribe, getPickupSelection, () => INITIAL);

/** The delivery step is answered: the shopper chose "To your door", or chose
 * pickup and picked a store for the current SKU. */
export const isDeliveryAnswered = (
  selection: PickupSelection,
  sku: string | null,
): boolean => {
  if (!selection.answered) return false;
  if (selection.mode === 'ship') return true;
  return sku !== null && selection.store?.sku === sku;
};

const BUY_PAGE = { entry: 'buy_page', sku_mode: 'exact' } as const;

export const setPickupMode = (mode: PickupMode): void => {
  if (state.mode === mode) {
    // Tapping the current card is still an answer (To your door, by default).
    if (!state.answered) set({ answered: true });
    return;
  }
  trackPickupEvent(BB_PICKUP_GTM_EVENTS.CHANGE, {
    ...BUY_PAGE,
    change: mode === 'pickup' ? 'to_pickup' : 'to_ship',
  });
  // Back to shipping frees whatever the backend was holding for us.
  if (mode === 'ship') releaseStoredHold();
  set({ mode, answered: true });
};

export const selectPickupStore = (choice: PickupChoice): void => {
  const previous = state.store;
  const sameStore =
    previous?.location_id === choice.location_id && previous.sku === choice.sku;
  if (!sameStore) {
    // A hold on another store or SKU is no use any more.
    const hold = readStoredHold();
    if (
      hold &&
      (hold.location_id !== choice.location_id || hold.sku !== choice.sku)
    ) {
      releaseStoredHold();
    }
    trackPickupEvent(BB_PICKUP_GTM_EVENTS.STORE_SELECTED, {
      ...BUY_PAGE,
      // The 3-digit area only: a full ZIP is a quasi-identifier.
      area_prefix: choice.zip.slice(0, 3),
      location_id: choice.location_id,
      sku: choice.sku,
    });
    if (previous) {
      trackPickupEvent(BB_PICKUP_GTM_EVENTS.CHANGE, {
        ...BUY_PAGE,
        change: 'store',
        area_prefix: choice.zip.slice(0, 3),
      });
    }
  }
  try {
    sessionStorage.setItem(BB_PICKUP_ZIP_STORAGE_KEY, choice.zip);
  } catch {}
  set({ store: choice });
};

/** Keeps the same store but re-targets it (new SKU or ZIP) after a refetch
 * confirmed it still has stock. Silent: the shopper did not choose again. */
export const refreshPickupChoice = (choice: PickupChoice): void => {
  const hold = readStoredHold();
  if (hold && (hold.location_id !== choice.location_id || hold.sku !== choice.sku)) {
    releaseStoredHold();
  }
  set({ store: choice });
};

/** Drops a choice that no longer applies (other SKU, store out of stock). */
export const clearPickupStore = (): void => {
  if (!state.store) return;
  const hold = readStoredHold();
  if (hold) releaseStoredHold();
  set({ store: null });
};

export const requestPickupStorePrompt = (): void => {
  set({ promptNonce: state.promptNonce + 1 });
};

/** Add-to-cart verdict for the current selection. `ship` also covers a
 * pickup choice that cannot apply (sizing kit, engraving): the selector
 * shows it disabled and the order ships. */
export type PickupAddDecision =
  | { kind: 'ship' }
  | { kind: 'need_store' }
  | { kind: 'pickup'; choice: PickupChoice };

export const decidePickupForAdd = (sku: string | null): PickupAddDecision => {
  ensurePickupSelectionHydrated();
  if (!BEST_BUY_PICKUP_ENABLED || !sku || state.mode !== 'pickup') {
    return { kind: 'ship' };
  }
  if (!state.store || state.store.sku !== sku) return { kind: 'need_store' };
  return { kind: 'pickup', choice: state.store };
};

/** True while this selection would go out as a pickup order. The express
 * (Apple Pay) checkout builds its own cart without the pickup properties and
 * hold, so callers hide it while this holds. */
export const usePickupChosen = (args: {
  store: string | null | undefined;
  color: RingProColors | null;
  size: RingSizes | null;
  engraving: boolean;
}): boolean => {
  const selection = usePickupSelection();
  // Re-render when the catalogue lands: pickupSkuFor reads it.
  usePickupCatalogue(args.store === ShopifyStore.US);
  useEffect(() => {
    ensurePickupSelectionHydrated();
  }, []);
  // Same gate as the add-to-cart path: pickup must actually apply.
  return selection.mode === 'pickup' && pickupSkuFor(args) !== null;
};
